/**
 * Turns what the controller said (as the speech model heard it) into an exact clearance.
 * The model passes callsigns and fixes through as heard — "Wizz Air one two three",
 * "whiskey zulu zulu 123", "W-Z-Z 123", "ma gel" — and this module matches them against the
 * aircraft on frequency and the airport's fixes. Anything not matched to exactly one
 * candidate is unresolved, and the pilot asks for a repeat instead of guessing.
 * ASSUMPTION: the fuzzy limits (one edit on 5+ letter fixes, two on long airline names) are
 * tuned by ear for gpt-4o-mini-transcribe; widen them only with real transcripts in hand.
 */
import { NATO, telephony, validateClearance, type Clearance } from '@atc/sim';

const DIGITS: Record<string, string> = {
  zero: '0', oh: '0', one: '1', won: '1', two: '2', too: '2', three: '3', tree: '3', four: '4', fower: '4',
  five: '5', fife: '5', six: '6', seven: '7', eight: '8', ait: '8', nine: '9', niner: '9',
};
const LETTERS: Record<string, string> = Object.fromEntries([
  ...Object.entries(NATO).map(([letter, word]) => [word.replace('-', ''), letter]),
  ['alfa', 'A'], ['juliet', 'J'], ['whisky', 'W'], ['xray', 'X'],
]);
/** Words a pilot or controller may add around a callsign that are not part of it. */
const FILLER = new Set(['heavy', 'super', 'the', 'and', 'uh', 'um']);

export interface ResolveContext {
  aircraft: readonly { callsign: string; airline: string }[];
  fixes: readonly string[];
  telephony?: Record<string, string>;
  /**
   * The aircraft the controller is already working, used only when the transmission carries no
   * callsign at all (app/src/conversation.ts). A callsign that was spoken but matched nothing
   * still fails: guessing there would put a clearance on the wrong aircraft.
   */
  partner?: string;
  /**
   * The aircraft picked on the radar. Selecting a blip is a deliberate "this one", so it does two
   * things a remembered partner may not: it breaks a tie between aircraft that read alike, and it
   * stands in for a callsign the transcript lost entirely (`resolveSpokenCallsign`). A callsign
   * that was heard *and* matched another aircraft still wins — the selection never overrides one.
   */
  selected?: string;
}

export type Resolution =
  /** `assumed` marks a clearance addressed to the selected aircraft after the heard callsign matched nothing. */
  | { ok: true; clearance: Clearance; correction: boolean; assumed?: true }
  /** `callsign` is set when the aircraft was recognised but the rest was not, so that pilot asks. */
  | { ok: false; callsign?: string; reason: string };

function words(heard: string): string[] {
  return heard.toLowerCase().replace(/[-_.,/]/g, ' ').replace(/([a-z])(\d)|(\d)([a-z])/g, '$1$3 $2$4').split(/\s+/).filter((w) => w && !FILLER.has(w));
}

const TEENS: Record<string, string> = { ten: '10', eleven: '11', twelve: '12', thirteen: '13', fourteen: '14', fifteen: '15', sixteen: '16', seventeen: '17', eighteen: '18', nineteen: '19' };
const TENS: Record<string, string> = { twenty: '2', thirty: '3', forty: '4', fourty: '4', fifty: '5', sixty: '6', seventy: '7', eighty: '8', ninety: '9' };
const REPEAT: Record<string, number> = { double: 2, triple: 3 };

/**
 * Words to exact callsign characters, or null if any word is not a digit, letter or short letter
 * group. Flight numbers come digit by digit (ICAO: "three zero three"), with "oh" for zero, or
 * grouped as FAA crews say them ("twelve thirty-four", "three fifty-one", "twelve hundred"), and
 * "double"/"triple" repeat the next digit ("double oh seven").
 */
function toChars(ws: readonly string[]): string | null {
  let out = '';
  for (let i = 0; i < ws.length; i++) {
    const w = ws[i]!;
    if (/^\d+$/.test(w)) out += w;
    else if (DIGITS[w]) out += DIGITS[w];
    else if (TEENS[w]) out += TEENS[w];
    else if (TENS[w]) {
      const unit = DIGITS[ws[i + 1] ?? ''];
      if (unit && unit !== '0') { out += TENS[w] + unit; i++; } else out += `${TENS[w]}0`;
    } else if (w === 'hundred' && out) out += '00';
    else if (w === 'thousand' && out) out += '000';
    else if (REPEAT[w] && DIGITS[ws[i + 1] ?? '']) { out += DIGITS[ws[i + 1]!]!.repeat(REPEAT[w]!); i++; }
    else if (LETTERS[w]) out += LETTERS[w];
    else if (/^[a-z]{1,3}$/.test(w)) out += w.toUpperCase();
    else return null;
  }
  return out;
}

export function editDistance(a: string, b: string): number {
  const row = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    let prev = row[0]!; row[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cur = row[j]!;
      row[j] = Math.min(row[j]! + 1, row[j - 1]! + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1));
      prev = cur;
    }
  }
  return row[b.length]!;
}

/**
 * Every aircraft the heard words could name, best tier first. A transcript rarely gets a flight
 * number perfectly: "El Al 701" for ELY791 and "Aegean 886" for AEE86 are both real misses from
 * session logs. So an exact number is tried across all traffic first, and only if nothing matches
 * exactly are the near misses considered — a correct callsign can never lose to a fuzzy one.
 *
 * ASSUMPTION: this stays safe because `spawner.callsign()` already refuses to put two callsigns
 * that read alike (same number, one digit changed, digits reordered) on frequency together, so a
 * single near miss usually has exactly one candidate. Where it does not, nothing is returned.
 */
export function matchCallsigns(heard: string, ctx: ResolveContext): string[] {
  const ws = words(heard);
  if (!ws.length) return [];
  const spelt = toChars(ws);

  /** Airline name and flight number as heard, for each way of splitting the words. */
  const splits = (ac: { callsign: string; airline: string }): { heardNumber: string }[] => {
    const name = telephony(ac.airline, ctx.telephony).toLowerCase().replace(/[^a-z]/g, '');
    if (!name) return [];
    const allowed = name.length >= 6 ? 2 : name.length >= 4 ? 1 : 0;
    const out: { heardNumber: string }[] = [];
    for (let k = 1; k <= Math.min(3, ws.length - 1); k++) {
      if (editDistance(ws.slice(0, k).join(''), name) > allowed) continue;
      const heardNumber = toChars(ws.slice(k));
      if (heardNumber !== null) out.push({ heardNumber });
    }
    return out;
  };

  const tier = (ac: { callsign: string; airline: string }, level: 1 | 2 | 3): boolean => {
    const number = ac.callsign.slice(ac.airline.length);
    if (level === 1) {
      if (spelt === ac.callsign) return true;
      return splits(ac).some((x) => x.heardNumber === number);
    }
    // One digit misheard: same length, one substitution ("701" for "791").
    if (level === 2) return splits(ac).some((x) => x.heardNumber.length === number.length && editDistance(x.heardNumber, number) === 1);
    // One digit too many, usually a doubled one ("886" for "86").
    return splits(ac).some((x) => x.heardNumber.length === number.length + 1 && editDistance(x.heardNumber, number) === 1);
  };

  for (const level of [1, 2, 3] as const) {
    const hits = ctx.aircraft.filter((ac) => tier(ac, level)).map((ac) => ac.callsign);
    if (hits.length) return hits;
  }
  return [];
}

/**
 * Callsign from heard words, only when exactly one aircraft matches — except that the aircraft
 * selected on the radar breaks a tie between candidates, since the controller has already said
 * which one they mean. Never guesses between candidates otherwise.
 */
export function resolveCallsign(heard: string, ctx: ResolveContext): string | null {
  const matches = matchCallsigns(heard, ctx);
  if (matches.length === 1) return matches[0]!;
  if (ctx.selected && matches.includes(ctx.selected)) return ctx.selected;
  return null;
}

/**
 * The same, for a field that is nothing but a callsign (the speech model's `spokenCallsign`, or a
 * misheard address in front of an instruction). With an aircraft selected on the radar, a heard
 * callsign that matches nothing at all is treated as that aircraft and reported as `assumed`, so
 * the UI can say so; the pilot reads back with its own callsign either way. Nothing is assumed
 * when the heard callsign did match aircraft — it is then ambiguous, not lost.
 */
export function resolveSpokenCallsign(heard: string, ctx: ResolveContext): { callsign: string | null; assumed: boolean } {
  const callsign = resolveCallsign(heard, ctx);
  if (callsign) return { callsign, assumed: false };
  if (ctx.selected && !matchCallsigns(heard, ctx).length) return { callsign: ctx.selected, assumed: true };
  return { callsign: null, assumed: false };
}

/** Rough sound-alike key: same first letter, similar consonants merged, vowels and doubles dropped. */
function soundKey(s: string): string {
  const t = s.toUpperCase().replace(/PH/g, 'F').replace(/[CQ]/g, 'K').replace(/Z/g, 'S').replace(/[DT]/g, 'T').replace(/[BP]/g, 'P').replace(/[VW]/g, 'V');
  return t[0] + t.slice(1).replace(/[AEIOUYH]/g, '').replace(/(.)\1+/g, '$1');
}

/** Fix from heard words: exact name, phonetic spelling ("bravo golf november"), split syllables, or one close sound-alike. */
export function resolveFix(heard: string, fixes: readonly string[]): string | null {
  const exact = heard.trim().toUpperCase();
  if (fixes.includes(exact)) return exact;
  const ws = words(heard);
  const spelt = toChars(ws);
  if (spelt && fixes.includes(spelt)) return spelt;
  const joined = ws.join('').toUpperCase();
  if (fixes.includes(joined)) return joined;
  if (joined.length < 4) return null;
  const close = fixes.filter((f) => f.length >= 5 && (editDistance(joined, f) <= 1 || soundKey(joined) === soundKey(f)));
  return close.length === 1 ? close[0]! : null;
}

/** Heard tool arguments → exact, validated clearance. Never guesses between candidates. */
export function resolveHeardClearance(raw: unknown, ctx: ResolveContext): Resolution {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { ok: false, reason: 'Transmission not understood.' };
  const r = raw as Record<string, unknown>;
  const heard = typeof r.spokenCallsign === 'string' && r.spokenCallsign.length <= 80 ? r.spokenCallsign.trim() : '';
  // Nothing that could be a callsign was said: carry on with the aircraft already being worked.
  if (!heard) {
    if (!ctx.partner) return { ok: false, reason: 'No callsign heard.' };
  }
  const spoken = heard ? resolveSpokenCallsign(heard, ctx) : { callsign: ctx.partner!, assumed: false };
  const callsign = spoken.callsign;
  if (!callsign) return { ok: false, reason: `No single aircraft matches "${heard}".` };
  if (!Array.isArray(r.instructions)) return { ok: false, callsign, reason: 'No instruction heard.' };
  const instructions: unknown[] = [];
  for (const item of r.instructions) {
    if (item && typeof item === 'object' && 'fix' in item && typeof (item as { fix: unknown }).fix === 'string') {
      const heardFix = (item as { fix: string }).fix;
      const fix = resolveFix(heardFix, ctx.fixes);
      if (!fix) return { ok: false, callsign, reason: `Fix "${heardFix}" not recognised.` };
      instructions.push({ ...item, fix });
    } else instructions.push(item);
  }
  const valid = validateClearance({ callsign, instructions });
  return valid.ok ? { ok: true, clearance: valid.clearance, correction: r.readbackCorrection === true, ...(spoken.assumed ? { assumed: true as const } : {}) } : { ok: false, callsign, reason: valid.message };
}
